import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Link as LinkIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

interface CreatePostDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (postData: any) => Promise<void>;
  classId: number;
}

interface Attachment {
  attachment_type: string;
  attachment_url: string;
  attachment_name: string;
}

const CreatePostDialog: React.FC<CreatePostDialogProps> = ({ open, onClose, onSubmit, classId }) => {
  const { t } = useTranslation();
  const [postType, setPostType] = useState('announcement');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [newAttachmentUrl, setNewAttachmentUrl] = useState('');
  const [newAttachmentName, setNewAttachmentName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setPostType('announcement');
    setTitle('');
    setContent('');
    setAttachments([]);
    setNewAttachmentUrl('');
    setNewAttachmentName('');
  };

  const handleAddAttachment = () => {
    if (newAttachmentUrl.trim()) {
      setAttachments([
        ...attachments,
        {
          attachment_type: 'link',
          attachment_url: newAttachmentUrl.trim(),
          attachment_name: newAttachmentName.trim() || newAttachmentUrl.trim()
        }
      ]);
      setNewAttachmentUrl('');
      setNewAttachmentName('');
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        post_type: postType,
        title: title.trim() || null,
        content: content.trim(),
        attachments: attachments,
        is_pinned: false
      });
      resetForm();
    } catch (err) {
      alert(t('classFeed.errorCreatingPost'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('classFeed.createPost')}</Typography>
          <IconButton 
            onClick={handleClose} 
            disabled={submitting}
            aria-label="Close dialog"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Post Type */}
          <FormControl fullWidth>
            <InputLabel>{t('classFeed.postType')}</InputLabel>
            <Select
              value={postType}
              label={t('classFeed.postType')}
              onChange={(e) => setPostType(e.target.value)}
            >
              <MenuItem value="announcement">
                📢 {t('classFeed.postTypes.announcement')}
              </MenuItem>
              <MenuItem value="material">
                📚 {t('classFeed.postTypes.material')}
              </MenuItem>
              <MenuItem value="assignment_link">
                📝 {t('classFeed.postTypes.assignment_link')}
              </MenuItem>
            </Select>
          </FormControl>

          {/* Title */}
          <TextField
            label={t('classFeed.title')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            placeholder={t('classFeed.titlePlaceholder')}
            helperText={t('classFeed.titleHelper')}
          />

          {/* Content */}
          <TextField
            label={t('classFeed.content')}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            fullWidth
            multiline
            rows={6}
            required
            placeholder={t('classFeed.contentPlaceholder')}
          />

          {/* Attachments Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('classFeed.attachments')} ({attachments.length})
            </Typography>

            {/* Existing Attachments */}
            {attachments.length > 0 && (
              <List dense sx={{ mb: 2 }}>
                {attachments.map((attachment, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <LinkIcon fontSize="small" sx={{ mr: 1 }} />
                    <ListItemText
                      primary={attachment.attachment_name}
                      secondary={attachment.attachment_url}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveAttachment(index)}
                        size="small"
                        aria-label={`Remove attachment ${attachment.attachment_name}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            {/* Add Attachment */}
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <TextField
                label={t('classFeed.attachmentUrl')}
                value={newAttachmentUrl}
                onChange={(e) => setNewAttachmentUrl(e.target.value)}
                fullWidth
                size="small"
                placeholder="https://..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddAttachment();
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label={t('classFeed.attachmentName')}
                  value={newAttachmentName}
                  onChange={(e) => setNewAttachmentName(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={t('classFeed.attachmentNamePlaceholder')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAttachment();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddAttachment}
                  disabled={!newAttachmentUrl.trim()}
                  startIcon={<AddIcon />}
                  aria-label="Add attachment"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('classFeed.addAttachment')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={submitting}
          aria-label="Cancel"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!content.trim() || submitting}
          aria-label={submitting ? 'Posting' : 'Post'}
        >
          {submitting ? t('common.posting') : t('classFeed.post')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePostDialog;

